import { Tags, SvgParams } from "./constants/constants";
import { AreaType } from "./interfaces/IAreaType";
import { ElementSide } from "./interfaces/IElementSide";
import { IBarDetails } from "./interfaces/specs/IBarSpec";
import { IColumnDetails } from "./interfaces/specs/IColumnSpec";
import { ICommonSpec } from "./interfaces/specs/ICommonSpec";
import { IFrontDetails } from "./interfaces/specs/IFrontSpec";
import { IGlassSpec } from "./interfaces/specs/IGlassSpec";
import { IMarquiseSpec } from "./interfaces/specs/IMarquiseSpec";
import { IRoofWindowSpec } from "./interfaces/specs/IRoofWindowSpec";
import { BomSideElements, BomRoofElements, BomRoofBarsTypes, PrintoutType, BomElements } from "./models/boms/bom.model";
import { Glass } from "./models/glasses/glass.model";
import { RoofWindow } from "./models/glasses/roofWindow.model";
import { Marquise } from "./models/marquises/marquise.model";
import { BarProfile } from "./models/profiles/bar.model";
import { ColumnProfile } from "./models/profiles/column.model";
import { ProfileConnector } from "./models/profiles/connector.model";
import { FrontProfile } from "./models/profiles/front.model";
import { SideFinishProfile } from "./models/profiles/side-finish.model";
import { WallProfile } from "./models/profiles/wall.model";
import { ProjectSpecification } from "./models/project-specification.model";
import { ElementType } from "./models/ToolboxModel";
import { LocationService } from "./services/location.service";
import { ProjectService } from "./services/project.service";
import { v4 as uuidv4 } from 'uuid';

export class Exporter {

    private images = [];
    private extras: ICommonSpec[] = [];

    constructor(private projectService: ProjectService, private locationService: LocationService) { }

    collect(area: AreaType) {
        const spec = new ProjectSpecification(this.projectService.profileService, area);
        spec.setup();

        const bom = new BomSideElements(
        spec.glassWalls,
        spec.extras,
        spec.glassWallDiamonds,
        spec.marquises,
        spec.doors,
        spec.walls,
        spec.comments
        )

        return bom;
    }

      private findSpecByGlass(glasses: IGlassSpec[], glass: Glass): IGlassSpec {
		if (glass == null) {
			return null;
		}
		return glasses.find(l => l.configId == glass.configId && Math.round(l.width) == Math.round(glass.width) && Math.round(l.height) == Math.round(glass.height));
	}

    collectRoof() {
      const spec = new ProjectSpecification(this.projectService.profileService, AreaType.Roof);
      spec.setup();
  
      const externals: IBarDetails[] = [];
      this.projectService.profileService.roofElements[ElementType.SideFinish]
        .filter((s: SideFinishProfile) => s.inBOM)
        .forEach((s: SideFinishProfile) => {
        externals.push({ configId: s.configId, width: Math.round(s.getLength()), location: this.locationService.getLocation(s, AreaType.Roof), cuttingOnSite: s.cuttingOnSite, hasLed: false});
      });
  
      const internals: IBarDetails[] = [];
      this.projectService.profileService.roofElements[ElementType.Bar]
        .filter((s: BarProfile) => s.inBOM)
        .forEach((s: BarProfile) => {
          internals.push({ configId: s.configId, width: Math.round(s.getLength()), location: this.locationService.getLocation(s, AreaType.Roof), cuttingOnSite: s.cuttingOnSite, hasLed: s.hasLedStripe(this.projectService.profileService) });
        });

      const columns: IColumnDetails[] = [];
      this.projectService.profileService.roofElements[ElementType.Column]
        .filter((s: ColumnProfile) => s.inBOM)
        .forEach((s: ColumnProfile) => {
          let h = Math.round(s.getHeight());
          columns.push({
            configId: s.configId,
          height: h,
          location: this.locationService.getLocation(s, AreaType.Roof),
          isRear: s.isRear,
          cuttingOnSite: s.cuttingOnSite,
          outflow: s.outflow != ElementSide.None 
          });
      });
  
      const fronts: IFrontDetails[] = [];
      this.projectService.profileService.roofElements[ElementType.Front]
        .filter((s: FrontProfile) => s.inBOM)
        .forEach((s: FrontProfile) => {
          fronts.push({
            configId: s.configId,
            width: s.getLength(),
            location: this.locationService.getLocation(s, AreaType.Roof),
            cuttingOnSite: s.cuttingOnSite,
          left: s.tag == Tags.LEFT_FRONT_TAG,
          right: s.tag == Tags.RIGHT_FRONT_TAG
        });
      });
  
      const rears: IFrontDetails[] = [];
      this.projectService.profileService.roofElements[ElementType.WallProfile]
        .filter((s: WallProfile) => s.inBOM)
        .forEach((s: WallProfile) => {
          var r: IFrontDetails = {
            configId: s.configId,
            width: Math.round(s.getLength()),
            location: this.locationService.getLocation(s, AreaType.Roof),
            cuttingOnSite: s.cuttingOnSite,
          left: s.tag == Tags.LEFT_REAR_TAG,
          right: s.tag == Tags.RIGHT_REAR_TAG
        };
        if(s.wallGutters?.length > 0) {
          r.wallGutters = [];
          s.wallGutters.forEach(sq => {
            r.wallGutters.push(sq.configId);
          });
        }
  
        rears.push(r);
      });
  
  
      const rear = this.projectService.template.getDefaultRear();
      const marquiseTopBottomCorrection: number = rear.marquiseTopBottomCorrection;
      const marquises: IMarquiseSpec[] = []
      const marquiseTypes = [ElementType.MarquiseTopUp, ElementType.MarquiseTopBottom];
      marquiseTypes.forEach(t => {
        this.projectService.profileService.roofElements[t]
          .filter((m: Marquise) => m.inBOM)
          .forEach(m => {
          const m2 = m as Marquise;
          const color = m2.fabric ? (m2.fabric["name"] ? m2.fabric["name"] : m2.fabric["description"]) : null;
          const ln = m.getMarquiseOnSide(ElementSide.Left, this.projectService.profileService.roofElements);
          const rn = m.getMarquiseOnSide(ElementSide.Right, this.projectService.profileService.roofElements);
          marquises.push({
            id: uuidv4(),
            type: m2.type,
            width: m2.getWidth(),
            depth: m2.getErpDepth(),
            configId: m2.configId,
            color: color?.trim(),
            engineId: m2.engine.configId,
            handlers: [ m2.leftHandler.id, m2.rightHandler.id],
            leftMarqId: ln?.id,
            rightMarqId: rn?.id,
            isReserved: false
          });
        });
      });
  
      var connectors: IBarDetails[] = [];
      var roofWindows: IRoofWindowSpec[] = [];
      var roofGutters: IRoofWindowSpec[] = [];

      this.projectService.profileService.roofElements[ElementType.ProfileConnector]
        .forEach((s: ProfileConnector) => {
          connectors.push({ configId: s.configId, width: Math.round(s.length), location: Math.round(this.locationService.getLocation(s, AreaType.Roof)), cuttingOnSite: s.cuttingOnSite });
        });

      const types = [ElementType.RoofWindow, ElementType.RoofGutter];
      types.forEach(t => {
        this.projectService.profileService.roofElements[t]
          .filter((m: RoofWindow) => m.inBOM)
          .forEach((m: RoofWindow) => {				
  
          const rw: IRoofWindowSpec = {
            configId: m.configId,
            width: Math.round(m.width),
            location: Math.round((m.rect.y - SvgParams.START_Y) / SvgParams.SCALE),
            bottomGlass: this.findSpecByGlass(spec.glasses, m.bottomGlass),
            topGlass: this.findSpecByGlass(spec.glasses, m.topGlass)
          };
          if (t == ElementType.RoofWindow) {
            roofWindows.push(rw);
          } else {
            roofGutters.push(rw);
          }
          if (m.connectorBottom.visible) {
            connectors.push({ configId: m.connectorConfigId, width: Math.round(m.bottomGlass.width), location: Math.round(m.connectorBottom.rect.y), cuttingOnSite: false });
          }
          if (m.connectorTop.visible) {
            connectors.push({ configId: m.connectorConfigId, width: Math.round(m.topGlass.width), location: Math.round(m.connectorTop.rect.y), cuttingOnSite: false });
          }
  
        });
      });
  
      var leXport: ICommonSpec[] = [];
      const stripesCount = this.projectService.profileService.roofElements[ElementType.LedStripe].length;
      if (stripesCount > 0) {
        const l = this.projectService.profileService.roofElements[ElementType.LedStripe][0];
        leXport.push({ configId: l.configId, count: stripesCount, length: Math.round(l.length), id: l.id, note: `${stripesCount} x ${l.length}` });
      };
  
      const bom = new BomRoofElements(
        spec.glasses,
        new BomRoofBarsTypes(externals, internals),
        connectors,
        columns,
        rears,
        fronts,
        marquises,
        roofWindows,
        roofGutters,
        spec.vents,
        spec.wallGuts,
        leXport,
        this.projectService.ledPattern?.patternId,
        spec.comments
      );
  
      var glView: IGlassSpec[] = [];
  
      spec.glasses.forEach((g: IGlassSpec) => {
        var gv = glView.find(v => v.configId == g.configId && v.height == g.height && v.width == g.width && g.areaType == v.areaType);
        if (gv) {				
          gv.count += g.count;
        } else {
          glView.push(g);
        }
      });
  
      return bom;
    }

    private getBoms() {
        var boms: any = new BomElements();

        boms.roof = this.collectRoof();
        boms.rear = this.collect(AreaType.Rear);
        boms.front = this.collect(AreaType.Front);
        boms.left = this.collect(AreaType.Left);
        boms.right = this.collect(AreaType.Right);

        return boms;
    }

    collectBomToSend() {
        var boms = this.getBoms();

		const t = this.projectService.template;
		// const boms: any = this.boms;

		const main = {
			id: this.projectService.productId,
			snowZone: t.snowZone,
			windZone: t.windZone,
			orderCode: this.projectService.orderCode,
			color: this.projectService.selectedColor.name, // ?? this.projectService.selectedColor.description,
			isStandalone: this.projectService.template.isStandalone,
			sizes: {
				width: t.width,
				depth: t.depth,
				backHeight: t.backHeight,
				frontHeight: Math.trunc(this.projectService.template.frontHeight),
				angle: this.projectService.template.dropAngle
			},
			customerName: this.projectService.info?.customerName,
			country: this.projectService.country,
		}

		const toSend = {
			boms,
			main,
			images: this.images,
			extras: this.extras
		};

		return toSend;
	}

}